'use client';

import { useEffect, useState, type RefObject } from 'react';

/** Fixed site header (~80px) plus breathing room. */
const HEADER_OFFSET_PX = 112;
const GAP_PX = 24;

/**
 * The `top` for a pinned side panel (spec 013, R2).
 *
 * Pins the panel BELOW the fixed header when it fits the viewport, and pins its
 * BOTTOM to the viewport bottom when it is taller: `top` goes negative by
 * exactly the overflow, so the panel's last controls are always reachable.
 * Un-pinning a tall panel instead would make it jump out of view the moment
 * it grows while the visitor is scrolled.
 *
 * Shared by the listing's filter sidebar and the lot page's enquiry panel.
 */
export const useStickyTop = (ref: RefObject<HTMLElement | null>, enabled = true) => {
  const [top, setTop] = useState(HEADER_OFFSET_PX);

  useEffect(() => {
    const panel = ref.current;

    if (!enabled || !panel) return;

    const measure = () =>
      setTop(Math.min(HEADER_OFFSET_PX, window.innerHeight - panel.offsetHeight - GAP_PX));

    measure();

    const observer = new ResizeObserver(measure);

    observer.observe(panel);
    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [ref, enabled]);

  return top;
};
